import { useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraImage,
  ChakraText,
  FAIconName,
  FireflyButton,
  Text,
} from '@equip.health/ui';

import { useProviderSupervisor } from '~/lib/contexts/ProviderSupervisorContext';
import { useGetProviderAlertsQuery } from '~/store/services/providerDashboardV2';

import checkmarkIllustration from '../assets/checkmark-illustration.svg';
import {
  ALERTS_DEFAULT_VISIBLE_COUNT,
  CARD_CONTENT_MIN_HEIGHT,
  CARD_STYLES,
  EMPTY_STATE_COPY,
} from '../constants';
import DashboardCardEmptyState from '../DashboardCardEmptyState';
import DashboardCardErrorState from '../DashboardCardErrorState';
import AlertActions from './AlertActions';
import AlertRow from './AlertRow';
import AlertsSkeleton from './AlertsSkeleton';
import { isOpenAlert } from './alerts.util';
import { useReviewAlert } from './useReviewAlert';

const ProviderDashboardAlertsCard = () => {
  const { viewingProviderId, isReadOnly, isLoadingSupervisees } =
    useProviderSupervisor();
  const { reviewAlert, reviewingSignalId } = useReviewAlert();
  const [expandedProviderId, setExpandedProviderId] = useState<string | null>(
    null,
  );
  const showAll = expandedProviderId === viewingProviderId;

  const {
    data,
    isLoading: isLoadingAlerts,
    isError,
    isFetching,
    refetch,
  } = useGetProviderAlertsQuery(
    { providerId: viewingProviderId },
    { skip: isLoadingSupervisees },
  );
  const isLoading = isLoadingSupervisees || isLoadingAlerts;

  const alerts = useMemo(() => (data ?? []).filter(isOpenAlert), [data]);
  const alertCount = alerts.length;
  const hiddenCount = Math.max(0, alertCount - ALERTS_DEFAULT_VISIBLE_COUNT);
  const hasMoreThanDefault = hiddenCount > 0;
  const showMoreLabel = showAll ? 'Show less' : `Show ${hiddenCount} more`;
  const visibleAlerts = showAll
    ? alerts
    : alerts.slice(0, ALERTS_DEFAULT_VISIBLE_COUNT);

  const renderAlerts = () => {
    if (isLoading) return <AlertsSkeleton />;

    if (isError) {
      return (
        <DashboardCardErrorState
          variant="stacked"
          onReload={refetch}
          isReloading={isFetching}
        />
      );
    }

    if (alertCount === 0) {
      return (
        <DashboardCardEmptyState
          icon={
            <ChakraImage
              src={checkmarkIllustration}
              alt=""
              width="40px"
              height="40px"
            />
          }
          title={EMPTY_STATE_COPY.alerts.title}
          body={EMPTY_STATE_COPY.alerts.body}
        />
      );
    }

    return (
      <>
        {visibleAlerts.map((alert) => (
          <AlertRow
            key={alert.signalId}
            alert={alert}
            actions={
              !isReadOnly && (
                <AlertActions
                  alert={alert}
                  isDisabled={
                    reviewingSignalId !== null &&
                    reviewingSignalId !== alert.signalId
                  }
                  isReviewing={reviewingSignalId === alert.signalId}
                  onReview={reviewAlert}
                />
              )
            }
          />
        ))}

        {hasMoreThanDefault && (
          <ChakraFlex
            borderTop="1px solid"
            borderColor="neutral.line"
            padding="16px 0"
          >
            <FireflyButton
              id="alerts-show-more"
              aria-label={showMoreLabel}
              onClick={() =>
                setExpandedProviderId(showAll ? null : viewingProviderId)
              }
              variant="tertiary"
              size="small"
              leftIcon={
                showAll
                  ? FAIconName.chevronUpRegular
                  : FAIconName.chevronDownRegular
              }
            >
              {showMoreLabel}
            </FireflyButton>
          </ChakraFlex>
        )}
      </>
    );
  };

  return (
    <ChakraBox
      data-testid="provider-dashboard-alerts-card"
      {...CARD_STYLES}
      padding="22px 22px 8px"
    >
      <ChakraFlex alignItems="center" gap="10px" marginBottom="6px">
        <Text variant="h5">Patients that need immediate action</Text>
        {!isLoading && !isError && alertCount > 0 && (
          <ChakraFlex
            alignItems="center"
            justifyContent="center"
            minWidth="22px"
            height="22px"
            padding="0 7px"
            borderRadius="full"
            bg="critical.base"
            data-testid="alerts-count-badge"
          >
            <ChakraText fontSize="13px" fontWeight="600" color="white">
              {alertCount}
            </ChakraText>
          </ChakraFlex>
        )}
      </ChakraFlex>

      <ChakraFlex
        data-testid="alerts-content-area"
        direction="column"
        minHeight={CARD_CONTENT_MIN_HEIGHT.alerts}
        justifyContent={
          isLoading || isError || alertCount === 0 ? 'center' : 'flex-start'
        }
      >
        {renderAlerts()}
      </ChakraFlex>
    </ChakraBox>
  );
};

export default ProviderDashboardAlertsCard;
